import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function AluminumWindows() {
  const { t } = useTranslation('common');
  const [isVisible, setIsVisible] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [lightboxOpen]);

  const styles = [
    { name: t('aluminumWindows.style1'), desc: t('aluminumWindows.style1Desc'), icon: 'ri-layout-row-line' },
    { name: t('aluminumWindows.style2'), desc: t('aluminumWindows.style2Desc'), icon: 'ri-window-line' },
    { name: t('aluminumWindows.style3'), desc: t('aluminumWindows.style3Desc'), icon: 'ri-picture-in-picture-line' },
    { name: t('aluminumWindows.style4'), desc: t('aluminumWindows.style4Desc'), icon: 'ri-door-open-line' },
    { name: t('aluminumWindows.style5'), desc: t('aluminumWindows.style5Desc'), icon: 'ri-arrow-up-line' },
    { name: t('aluminumWindows.style6'), desc: t('aluminumWindows.style6Desc'), icon: 'ri-layout-grid-line' }
  ];

  const benefits = [
    { title: t('aluminumWindows.benefit1'), desc: t('aluminumWindows.benefit1Desc'), icon: 'ri-shield-line' },
    { title: t('aluminumWindows.benefit2'), desc: t('aluminumWindows.benefit2Desc'), icon: 'ri-contrast-line' },
    { title: t('aluminumWindows.benefit3'), desc: t('aluminumWindows.benefit3Desc'), icon: 'ri-paint-line' },
    { title: t('aluminumWindows.benefit4'), desc: t('aluminumWindows.benefit4Desc'), icon: 'ri-recycle-line' },
    { title: t('aluminumWindows.benefit5'), desc: t('aluminumWindows.benefit5Desc'), icon: 'ri-fire-line' },
    { title: t('aluminumWindows.benefit6'), desc: t('aluminumWindows.benefit6Desc'), icon: 'ri-tools-line' }
  ];

  const galleryImages = [
    {
      url: 'https://public.readdy.ai/ai/img_res/37c174cc38faaddaca259aa161685965.jpg',
      alt: 'Aluminum Sliding Window'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Elegant%20black%20aluminum%20casement%20window%20in%20stylish%20kitchen%20with%20soft%20morning%20light%20illuminating%20modern%20countertops%2C%20tranquil%20contemporary%20home%20interior%20with%20natural%20materials%20and%20gentle%20ambient%20glow%2C%20peaceful%20residential%20space%20featuring%20energy%20efficient%20Milgard%20aluminum%20window%20with%20sophisticated%20design%20and%20inviting%20atmosphere&width=800&height=600&seq=aluminum-gallery-002&orientation=landscape',
      alt: 'Aluminum Casement Window'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Beautiful%20aluminum%20picture%20window%20in%20bright%20home%20office%20with%20soft%20natural%20daylight%20and%20minimalist%20workspace%2C%20serene%20modern%20interior%20with%20clean%20architectural%20lines%20and%20warm%20ambient%20lighting%2C%20peaceful%20residential%20environment%20showcasing%20high%20quality%20Milgard%20aluminum%20window%20with%20delicate%20shadows%20and%20comfortable%20atmosphere&width=800&height=600&seq=aluminum-gallery-003&orientation=landscape',
      alt: 'Aluminum Picture Window'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Sophisticated%20aluminum%20awning%20window%20in%20contemporary%20bathroom%20with%20soft%20diffused%20light%20and%20spa-like%20ambiance%2C%20tranquil%20modern%20home%20interior%20with%20clean%20surfaces%20and%20gentle%20glow%2C%20peaceful%20residential%20space%20featuring%20premium%20Milgard%20aluminum%20window%20with%20sleek%20frames%20and%20inviting%20atmosphere&width=800&height=600&seq=aluminum-gallery-004&orientation=landscape',
      alt: 'Aluminum Awning Window'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Stylish%20aluminum%20fixed%20window%20in%20elegant%20dining%20room%20with%20soft%20natural%20daylight%20and%20refined%20furniture%2C%20serene%20contemporary%20interior%20with%20warm%20neutral%20tones%20and%20delicate%20ambient%20lighting%2C%20peaceful%20residential%20space%20showcasing%20Milgard%20aluminum%20window%20craftsmanship%20with%20modern%20design%20and%20comfortable%20atmosphere&width=800&height=600&seq=aluminum-gallery-005&orientation=landscape',
      alt: 'Aluminum Fixed Window'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Premium%20aluminum%20horizontal%20slider%20window%20in%20cozy%20bedroom%20with%20soft%20morning%20sunlight%20and%20comfortable%20bedding%2C%20tranquil%20modern%20home%20interior%20with%20warm%20pastel%20tones%20and%20gentle%20ambient%20glow%2C%20peaceful%20residential%20environment%20featuring%20quality%20Milgard%20aluminum%20window%20with%20slim%20profiles%20and%20inviting%20atmosphere&width=800&height=600&seq=aluminum-gallery-006&orientation=landscape',
      alt: 'Aluminum Horizontal Slider'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Contemporary%20aluminum%20double%20hung%20window%20in%20bright%20living%20space%20with%20soft%20natural%20light%20and%20modern%20furnishings%2C%20serene%20residential%20interior%20with%20clean%20design%20and%20warm%20ambient%20lighting%2C%20peaceful%20home%20showcasing%20energy%20efficient%20Milgard%20aluminum%20window%20with%20delicate%20shadows%20and%20comfortable%20atmosphere&width=800&height=600&seq=aluminum-gallery-007&orientation=landscape',
      alt: 'Aluminum Double Hung Window'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Elegant%20aluminum%20bay%20window%20in%20sophisticated%20reading%20nook%20with%20soft%20daylight%20and%20cozy%20seating%2C%20tranquil%20contemporary%20home%20interior%20with%20warm%20wood%20accents%20and%20gentle%20ambient%20glow%2C%20peaceful%20residential%20space%20featuring%20premium%20Milgard%20aluminum%20window%20with%20architectural%20beauty%20and%20inviting%20atmosphere&width=800&height=600&seq=aluminum-gallery-008&orientation=landscape',
      alt: 'Aluminum Bay Window'
    }
  ];

  const openLightbox = (index: number) => {
    setCurrentImage(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar isScrolled={true} />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <img 
          src="https://readdy.ai/api/search-image?query=Stunning%20modern%20home%20exterior%20featuring%20sleek%20black%20aluminum%20windows%20with%20contemporary%20architectural%20design%2C%20soft%20natural%20sunlight%20highlighting%20premium%20window%20frames%2C%20peaceful%20residential%20setting%20with%20minimalist%20landscaping%2C%20warm%20inviting%20atmosphere%20showcasing%20durable%20energy%20efficient%20aluminum%20windows%2C%20professional%20architectural%20photography%20with%20clean%20lines%20and%20serene%20ambiance&width=1920&height=1080&seq=aluminum-hero-901&orientation=landscape"
          alt="Milgard Aluminum Windows"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-900/60 via-stone-800/50 to-stone-900/70"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <p className="text-stone-200 text-xs font-semibold tracking-[0.2em] mb-4 uppercase">{t('aluminumWindows.badge')}</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-white mb-6 leading-tight">
            {t('aluminumWindows.heroTitle')}
          </h1>
          <p className="text-stone-100 text-base md:text-lg mb-10 font-light max-w-2xl mx-auto">
            {t('aluminumWindows.heroSubtitle')}
          </p>
          <a 
            href="/#contact"
            className="inline-flex items-center gap-3 bg-white text-stone-900 px-10 py-4 rounded-full text-sm font-medium hover:bg-stone-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            {t('aluminumWindows.ctaButton')}
            <i className="ri-arrow-right-line text-base"></i>
          </a>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-stone-800 mb-6">
              {t('aluminumWindows.overviewTitle')}
            </h2>
            <div className="w-12 h-px bg-amber-600 mx-auto mb-6"></div>
            <p className="text-stone-600 text-sm leading-relaxed">
              {t('aluminumWindows.overviewDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: t('aluminumWindows.feature1'), desc: t('aluminumWindows.feature1Desc'), icon: 'ri-building-line' },
              { title: t('aluminumWindows.feature2'), desc: t('aluminumWindows.feature2Desc'), icon: 'ri-pencil-ruler-line' },
              { title: t('aluminumWindows.feature3'), desc: t('aluminumWindows.feature3Desc'), icon: 'ri-shield-check-line' },
              { title: t('aluminumWindows.feature4'), desc: t('aluminumWindows.feature4Desc'), icon: 'ri-award-line' }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-lg mb-4">
                  <i className={`${feature.icon} text-amber-700 text-2xl`}></i>
                </div>
                <h3 className="text-base font-medium text-stone-800 mb-2">{feature.title}</h3>
                <p className="text-stone-600 text-xs leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Styles Section */}
      <section ref={sectionRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-stone-800 mb-4">
              {t('aluminumWindows.stylesTitle')}
            </h2>
            <div className="w-12 h-px bg-amber-600 mx-auto mb-6"></div>
            <p className="text-stone-600 text-sm max-w-2xl mx-auto">
              {t('aluminumWindows.stylesSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {styles.map((style, index) => (
              <div 
                key={index}
                className={`group bg-stone-50 rounded-xl p-6 hover:bg-amber-50 transition-all duration-300 cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-14 h-14 flex items-center justify-center bg-white rounded-lg mb-4 group-hover:bg-amber-100 transition-colors">
                  <i className={`${style.icon} text-amber-700 text-2xl`}></i>
                </div>
                <h3 className="text-base font-medium text-stone-800 mb-2">{style.name}</h3>
                <p className="text-stone-600 text-xs leading-relaxed">{style.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-stone-800 mb-4">
              {t('aluminumWindows.galleryTitle')}
            </h2>
            <div className="w-12 h-px bg-amber-600 mx-auto mb-6"></div>
            <p className="text-stone-600 text-sm max-w-2xl mx-auto">
              {t('aluminumWindows.gallerySubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {galleryImages.map((image, index) => (
              <div 
                key={index}
                onClick={() => openLightbox(index)}
                className="group relative overflow-hidden rounded-lg cursor-pointer bg-stone-200"
                style={{ width: '100%', height: '240px' }}
              >
                <img 
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full opacity-0 group-hover:opacity-100 transform scale-50 group-hover:scale-100 transition-all duration-300">
                    <i className="ri-search-line text-amber-700 text-xl"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer whitespace-nowrap z-50"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line text-2xl"></i>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-right-line text-2xl"></i>
          </button>

          <div 
            className="max-w-6xl max-h-[90vh] px-16"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={galleryImages[currentImage].url}
              alt={galleryImages[currentImage].alt}
              className="w-full h-full object-contain rounded-lg"
            />
            <p className="text-white text-center mt-4 text-sm">
              {galleryImages[currentImage].alt} ({currentImage + 1} / {galleryImages.length})
            </p>
          </div>
        </div>
      )}

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-stone-800 mb-4">
              {t('aluminumWindows.benefitsTitle')}
            </h2>
            <div className="w-12 h-px bg-amber-600 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-stone-50 rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-lg mb-4">
                  <i className={`${benefit.icon} text-amber-700 text-xl`}></i>
                </div>
                <h3 className="text-base font-medium text-stone-800 mb-2">{benefit.title}</h3>
                <p className="text-stone-600 text-xs leading-relaxed">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-light text-stone-800 mb-6">
            {t('aluminumWindows.ctaTitle')}
          </h2>
          <p className="text-stone-600 text-sm mb-10 max-w-2xl mx-auto">
            {t('aluminumWindows.ctaDesc')}
          </p>
          <a 
            href="/#contact"
            className="inline-flex items-center gap-3 bg-amber-700 text-white px-10 py-4 rounded-full text-sm font-medium hover:bg-amber-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            {t('aluminumWindows.ctaButton')}
            <i className="ri-arrow-right-line text-base"></i>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}